import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useClient } from "@/contexts/ClientContext";
import { toast } from "react-toastify";
import ApperIcon from "@/components/ApperIcon";
import Loading from "@/components/ui/Loading";
import Select from "@/components/atoms/Select";
import Button from "@/components/atoms/Button";
import AddClientModal from "@/components/organisms/AddClientModal";
import { cn } from "@/utils/cn";
import { useAuth } from "@/layouts/Root";

const Sidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const { clients, selectedClient, setSelectedClient, addClient, loading } = useClient();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Load saved client selection from localStorage
  React.useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      const savedClientId = localStorage.getItem('selectedClientId');
      if (savedClientId) {
        const savedClient = clients.find(c => c.Id === parseInt(savedClientId));
        if (savedClient) {
          setSelectedClient(savedClient);
        }
      }
    }
  }, [clients, selectedClient, setSelectedClient]);
const navigationItems = [
    {
      name: "Clients",
      href: "/clients",
      icon: "Building2",
      description: "Source of truth for brand, notes, and industries"
    },
    {
      name: "Templates",
      href: "/templates",
      icon: "Book",
      description: "Manage and organize ad templates"
    },
    {
      name: "Facebook Setup",
      href: "/facebook/login",
      icon: "Facebook",
      description: "Connect business, ad account, and page"
    },
    {
      name: "AI Builder",
      href: "/builder",
      icon: "Workflow",
      description: "Create campaigns, ad sets & ads together"
    },
    {
      name: "Ad Campaigns",
      href: "/campaigns",
      icon: "Layers",
      description: "Review saved builds, edit, and republish"
    }
  ];

const handleClientChange = (e) => {
    const clientId = parseInt(e.target.value);
    const client = clients.find(c => c.Id === clientId);
    setSelectedClient(client);
    // Save selected client to localStorage
    localStorage.setItem('selectedClientId', clientId.toString());
  };

  const handleAddClient = async (clientData) => {
    try {
      await addClient(clientData);
      toast.success("Client added successfully!");
      setIsAddClientModalOpen(false);
    } catch (error) {
      toast.error("Failed to add client. Please try again.");
    }
  };

  const SidebarContent = () => {

  const clientOptions = clients.map(client => ({
value: client.Id.toString(),
    label: client.companyName
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Client Selector */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Selected Client
            </label>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setIsAddClientModalOpen(true)}
              className="text-xs px-2 py-1 h-6"
            >
              <ApperIcon name="Plus" size={12} />
            </Button>
          </div>
          
{loading ? (
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <ApperIcon name="Loader2" size={16} className="animate-spin text-gray-400" />
              <span className="text-sm text-gray-600">Loading clients...</span>
            </div>
          ) : !clients || clients.length === 0 ? (
            <div className="p-2 bg-gray-50 rounded-lg text-center">
              <div className="text-sm text-gray-600 mb-2">
                {clients === undefined ? "Failed to load clients" : "No clients yet"}
              </div>
              <div className="text-xs text-gray-500">
                {clients === undefined ? "Check console for errors" : "Add your first client to get started"}
              </div>
            </div>
          ) : (
            <>
              {console.log('SIDEBAR_DEBUG: Rendering client dropdown with options:', clientOptions)}
              <Select
                value={selectedClient?.Id?.toString() || ""}
                onChange={handleClientChange}
                placeholder={`Select a client (${clients.length} available)`}
                options={clientOptions}
              />
            </>
          )}
        </div>
      </div>
      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {navigationItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
              isActive
                ? "bg-gradient-to-r from-facebook/10 to-facebook-hover/10 text-facebook border-l-4 border-facebook shadow-sm"
                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
)}
          >
            <ApperIcon 
              name={item.icon || "Circle"} 
              className="w-5 h-5" 
            />
            <div className="flex-1">
              <div>{item.name}</div>
              <div className="text-xs text-gray-500 group-hover:text-gray-600">
                {item.description}
              </div>
            </div>
          </NavLink>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg p-1.5">
              <ApperIcon name="Sparkles" className="w-4 h-4 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 text-sm">AI Assistant</h4>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Get AI-powered recommendations for better campaign performance.
          </p>
          <button
            onClick={() => navigate("/builder")}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-xs font-medium py-2 px-3 rounded-md transition-all duration-200 transform hover:scale-105"
          >
            Open Builder
          </button>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-700 hover:text-gray-900 hover:border-gray-300 text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200"
        >
          <ApperIcon name="LogOut" className="w-4 h-4" />
          Logout
        </button>
      </div>
</div>
  );
  };
  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-64 bg-white border-r border-gray-200 h-full">
        <SidebarContent />
      </div>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg border border-gray-200 shadow-sm"
      >
        <ApperIcon name="Menu" className="w-5 h-5 text-gray-700" />
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="absolute left-0 top-0 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-out">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-facebook to-facebook-hover rounded-lg p-1.5">
                  <ApperIcon name="Zap" className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-gray-900">Ratesight FB Ads AI</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md"
              >
                <ApperIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}
<AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onSubmit={handleAddClient}
      />
    </>
  );
};

export default Sidebar;